import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { useUnsavedChangesStore } from '../../state/useUnsavedChangesStore';

/**
 * Unsaved Changes Guard dialog
 *
 * Mounted once by ProjectDialogsHost. Renders the promise-based prompt from
 * useUnsavedChangesStore: "Save / Don't Save / Cancel" before a document
 * flow replaces the working document. Escape and the overlay behave as
 * Cancel (never silently discard).
 */
export function UnsavedChangesDialog() {
    const { t } = useTranslation();
    const isOpen = useUnsavedChangesStore((s) => s.isOpen);
    const close = useUnsavedChangesStore((s) => s.close);
    const cancelRef = useRef<HTMLButtonElement>(null);

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={() => close(false)}
            title={t('dialogs.unsaved.title')}
            variant="alert"
            initialFocusRef={cancelRef}
            footer={
                <>
                    <DialogButton ref={cancelRef} variant="secondary" onClick={() => close(false)}>
                        {t('dialogs.unsaved.cancel')}
                    </DialogButton>
                    <DialogButton variant="destructive" onClick={() => close(true)}>
                        {t('dialogs.unsaved.dontSave')}
                    </DialogButton>
                    <DialogButton variant="primary" onClick={async () => {
                        // Deferred import: the dialog must not pull the
                        // whole I/O layer into its render-time graph.
                        const { saveCurrentDocument } = await import('../../utils/ProjectFileIO');
                        const saved = await saveCurrentDocument();
                        close(saved);
                    }}>
                        {t('dialogs.unsaved.save')}
                    </DialogButton>
                </>
            }
        >
            {t('dialogs.unsaved.message')}
        </AppDialog>
    );
}
